import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, Check, Calendar, Clock } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { toast } from "sonner";

const Sessions = () => {
  const navigate = useNavigate();
  const { isPro } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listSessions();
      setSessions(r.data.items || []);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const undo = async (id) => {
    try {
      await api.updateSessionCompletion(id, false);
      toast("Completion undone");
      load();
    } catch { toast.error("Undo failed"); }
  };

  const redo = async (id) => {
    try {
      await api.updateSessionCompletion(id, true);
      toast.success("Marked complete");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="app-shell" data-testid="sessions-page">
      <Header />
      <section className="px-6 pt-2 pb-6">
        <button onClick={() => navigate(-1)} className="text-xs text-[#5C7568] flex items-center gap-1 mb-3" data-testid="back-btn">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Reset Sessions</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your practice trail
        </h2>
        <p className="text-sm text-[#5C7568] mt-2">
          {isPro ? `${sessions.length} sessions` : "Free shows today only. Pro unlocks full history."}
        </p>

        {loading ? (
          <p className="text-sm text-[#5C7568] mt-6">Loading…</p>
        ) : sessions.length === 0 ? (
          <div className="mt-6 rounded-3xl bg-white border border-[#E2EBE6] p-8 text-center">
            <p className="text-sm text-[#5C7568]">No sessions yet. Begin one from Today.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="sessions-list">
            {sessions.map((s) => (
              <div key={s.id} className="rounded-3xl bg-white border border-[#E2EBE6] p-5 shadow-[0_8px_32px_rgba(35,75,56,0.05)]" data-testid={`session-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#5C7568] flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {s.date} · <Clock className="w-3 h-3" /> {s.time}
                  </span>
                  {s.completed ? (
                    <span className="rounded-full bg-[#234B38] text-white text-[10px] font-semibold px-2.5 py-0.5 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Done
                    </span>
                  ) : (
                    <span className="rounded-full bg-[#F5D0D6]/60 text-[#D6336C] text-[10px] font-semibold px-2.5 py-0.5">
                      Undone
                    </span>
                  )}
                </div>
                <h3 className="text-base font-medium text-[#1A2E24] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {s.breathwork_name}
                </h3>
                {s.state && (
                  <p className="text-[11px] text-[#234B38] font-semibold uppercase tracking-wider mt-1">State · {s.state.replace("_", " ")}</p>
                )}
                <p className="text-sm text-[#5C7568] mt-2 line-clamp-2">"{s.mindset}"</p>
                <div className="flex gap-2 mt-4">
                  {s.completed ? (
                    <button onClick={() => undo(s.id)} data-testid={`undo-${s.id}`} className="rounded-full bg-white border border-[#E2EBE6] text-[#5C7568] px-4 py-2 text-xs font-semibold">
                      Undo
                    </button>
                  ) : (
                    <button onClick={() => redo(s.id)} data-testid={`redo-${s.id}`} className="rounded-full bg-[#234B38] text-white px-4 py-2 text-xs font-semibold">
                      Mark complete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Sessions;
